import { ChevronLeft, ChevronRight } from "lucide-react";
import { type ComponentProps, type HTMLAttributes, type ReactNode } from "react";

import { cn } from "@/lib/utils/cn";
import { IconButton } from "./button";

export type TableProps = ComponentProps<"table"> & { flush?: boolean; containerClassName?: string };

export function Table({ className, flush = false, containerClassName, ...props }: TableProps) {
  const table = <table className={cn("w-full min-w-[20rem] caption-bottom border-collapse text-left text-body text-foreground", className)} {...props} />;
  return flush ? table : <div className={cn("table-shell relative w-full overflow-x-auto rounded-card", containerClassName)}>{table}</div>;
}

export function TableFrame({ className, children, ...rest }: HTMLAttributes<HTMLDivElement> & { children: ReactNode }) {
  return (
    <div className={cn("table-shell relative w-full overflow-x-auto rounded-card", className)} {...rest}>
      <table className="w-full min-w-[20rem] border-collapse text-left text-caption text-foreground">{children}</table>
    </div>
  );
}

export function TableHeader({ className, ...props }: ComponentProps<"thead">) {
  return <thead className={cn("table-header-row border-b border-card-border bg-[var(--surface-card-inset)] [&_tr]:transition-colors", className)} {...props} />;
}

export function TableBody({ className, ...props }: ComponentProps<"tbody">) {
  return <tbody className={cn("table-data-rows divide-y divide-card-border [&_tr:last-child]:border-0", className)} {...props} />;
}

export function TableFooter({ className, ...props }: ComponentProps<"tfoot">) {
  return <tfoot className={cn("border-t border-card-border bg-[var(--surface-card-elevated)] text-caption font-medium", className)} {...props} />;
}

export function TableRow({ className, ...props }: ComponentProps<"tr">) {
  return (
    <tr
      className={cn(
        "table-data-row border-b border-card-border transition-colors duration-fast odd:bg-transparent even:bg-surface-secondary/40 last:border-b-0 hover:bg-surface-hover data-[state=selected]:bg-accent-muted",
        className,
      )}
      {...props}
    />
  );
}

export function TableHead({ className, ...props }: ComponentProps<"th">) {
  return <th className={cn("table-header-label h-[var(--density-row-compact)] whitespace-nowrap px-ds-4 text-left align-middle", className)} {...props} />;
}

export function TableCell({ className, ...props }: ComponentProps<"td">) {
  return <td className={cn("min-h-[var(--density-row-comfortable)] px-ds-4 py-ds-3 align-middle text-body text-foreground", className)} {...props} />;
}

export const THead = TableHeader;
export const TBody = TableBody;
export const Tr = TableRow;
export const Th = TableHead;
export const Td = TableCell;

export type TablePaginationProps = {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  totalItems?: number;
  pageSize?: number;
  className?: string;
  disabled?: boolean;
};

export function TablePagination({ page, pageCount, onPageChange, totalItems, pageSize, className, disabled }: TablePaginationProps) {
  const safePageCount = Math.max(1, pageCount);
  const safePage = Math.min(Math.max(1, page), safePageCount);
  const first = totalItems && pageSize ? (safePage - 1) * pageSize + 1 : null;
  const last = totalItems && pageSize ? Math.min(safePage * pageSize, totalItems) : null;
  return (
    <nav className={cn("flex min-h-[var(--density-row-compact)] items-center justify-between gap-ds-3 border-t border-card-border bg-[var(--surface-card-elevated)] px-ds-4 py-ds-2", className)} aria-label="Table pagination">
      <p className="text-caption tabular-nums text-muted">
        {first && last && totalItems ? `${first}–${last} of ${totalItems}` : `Page ${safePage} of ${safePageCount}`}
      </p>
      <div className="flex items-center gap-ds-1">
        <IconButton variant="ghost" aria-label="Previous page" disabled={disabled || safePage <= 1} onClick={() => onPageChange(safePage - 1)}><ChevronLeft className="h-4 w-4" /></IconButton>
        <IconButton variant="ghost" aria-label="Next page" disabled={disabled || safePage >= safePageCount} onClick={() => onPageChange(safePage + 1)}><ChevronRight className="h-4 w-4" /></IconButton>
      </div>
    </nav>
  );
}

export type StickyTableRegionProps = ComponentProps<"div"> & { side?: "left" | "right" | "top" };

export function StickyTableRegion({ side = "top", className, ...props }: StickyTableRegionProps) {
  return (
    <div
      className={cn(
        "sticky z-10 bg-[var(--surface-card)]",
        side === "top" && "top-0 border-b border-card-border",
        side === "left" && "left-0 border-r border-card-border shadow-[var(--table-pane-shadow)]",
        side === "right" && "right-0 border-l border-card-border shadow-floating",
        className,
      )}
      {...props}
    />
  );
}
